"use client"

import styled from "@emotion/styled"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
} from "@mui/material"
import Image from "next/image"
import { forwardRef, JSX, useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/navigation"
import { useInViewConfig } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Link, replaceRichTextLinks, subInfoClick } from "utils"

export const StyledAccordion = styled(Accordion)`
  background-color: ${sharedColors.white};
  border-radius: 16px !important;
  box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.08);
  margin: 0 !important;
  border: 1px solid ${sharedColors.borderGray} !important;

  &::before {
    display: none;
  }

  .MuiAccordionSummary-root {
    padding: 29px 12px 29px 12px !important;
    min-height: 72px !important;
    ${({ theme }) => theme.breakpoints.up("md")} {
      padding: 29px 24px 29px 24px !important;
    }
    &.Mui-expanded {
      box-shadow: inset 0 -1px 0 0 ${sharedColors.borderGray};
    }
    &:focus-visible {
      border: 1px dashed white;
      outline-offset: 2px;
      outline: 2px solid black;
    }
  }

  .MuiAccordionSummary-content {
    display: flex;
    align-items: center;
    margin: 0 !important;
    font-size: 18px !important;
    font-weight: ${({ expanded }) => (expanded ? 700 : 400)} !important;
    p {
      font-weight: ${({ expanded }) => (expanded ? 700 : 400)} !important;
    }
    line-height: 28px !important;
    color: ${sharedColors.black};
    ${({ theme }) => theme.breakpoints.up("md")} {
      font-size: 20px !important;
    }
  }

  .MuiAccordionDetails-root {
    padding: 16px 12px !important;
    display: flex;
    flex-direction: column;
    gap: 24px;
    ${({ theme }) => theme.breakpoints.up("md")} {
      gap: 24px;
      justify-content: space-between;
      flex-direction: row;
    }
  }

  .Mui-expanded {
    margin: 0 !important;
  }
  a {
    color: #051711;
  }
`

export const StyledTypographyWithTable = styled(Typography)`
  p {
    margin-top: 0;
  }
  h4 {
    margin: 0;
  }

  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin: 16px 0;
    overflow: hidden;
    border-radius: 8px;
    text-align: left;
  }

  th {
    background-color: ${sharedColors.brandDark};
    color: ${sharedColors.white};
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    padding: 16px 12px;
    border: none;
    ${({ theme }) => theme.breakpoints.up("md")} {
      font-size: 20px;
      line-height: 28px;
      padding: 16px 24px;
    }
  }

  /* When there's no th elements, treat the first row as header */
  table:not(:has(th)) tr:first-of-type td {
    background-color: ${sharedColors.brandDark};
    color: ${sharedColors.white};
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    padding: 16px 12px;
    ${({ theme }) => theme.breakpoints.up("md")} {
      font-size: 20px;
      line-height: 28px;
      padding: 16px 24px;
    }
  }

  /* Remove border-bottom from first row when acting as header */
  table:not(:has(th)) tr:first-of-type td {
    border-bottom: none;
  }

  /* First row acting as header shouldn't have hover effect */
  table:not(:has(th)) tr:first-of-type:hover {
    background-color: ${sharedColors.brandDark};
  }

  th p,
  td p {
    margin: 0;
  }

  td {
    font-size: 14px;
    line-height: 22px;
    padding: 16px 12px;
    border: none;
    color: ${sharedColors.black};
    ${({ theme }) => theme.breakpoints.up("md")} {
      font-size: 18px;
      line-height: 26px;
      padding: 16px 24px;
    }
  }

  tbody tr:hover {
    background-color: ${sharedColors.mediumGrayLight};
  }

  table a {
    color: ${sharedColors.black} !important;
    text-decoration: underline;
  }
`

const StyledButton = styled(Button)`
  width: 144px;
  align-self: flex-end;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 176px;
  }
`

interface AccordionItemProps {
  title: string
  content: string
  buttonLink?: string
  buttonText?: string
  expanded: boolean
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void
  pageTitle?: string
  accordionTitle?: string
}

const AccordionItemComp = forwardRef<HTMLDivElement, AccordionItemProps>(
  (
    {
      title,
      content,
      buttonLink,
      buttonText,
      expanded,
      onChange,
      accordionTitle,
      pageTitle,
    },
    ref,
  ): JSX.Element => {
    const { ref: inViewRef, inView } = useInView(useInViewConfig)
    const [isMounted, setIsMounted] = useState(false)

    useEffect(() => {
      setIsMounted(true)
    }, [])

    // Process the content to replace internal links
    const processedContent = replaceRichTextLinks(content)
    return (
      <StyledAccordion
        defaultExpanded={expanded}
        expanded={expanded}
        onChange={onChange}
        ref={ref}
      >
        <AccordionSummary
          expandIcon={
            expanded ? (
              <Image
                alt=""
                width={24}
                height={24}
                src={"/icons/accordionClose.svg"}
              />
            ) : (
              <Image
                alt=""
                width={24}
                height={24}
                src={"/icons/accordionOpen.svg"}
              />
            )
          }
        >
          <Typography variant="body1">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {isMounted && (
            <StyledTypographyWithTable
              variant="body2"
              dangerouslySetInnerHTML={{ __html: processedContent || "" }}
            />
          )}
          {buttonLink && buttonText && (
            <StyledButton
              variant="secondary"
              size="medium"
              LinkComponent={Link}
              href={buttonLink}
              onClick={() => {
                subInfoClick(buttonText, pageTitle || "", title)
              }}
            >
              {buttonText}
            </StyledButton>
          )}
        </AccordionDetails>
      </StyledAccordion>
    )
  },
)

AccordionItemComp.displayName = "AccordionItemComp"

export default AccordionItemComp
